import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { toQuery, useAnalyticsFilters } from './filters';
import { fmtMin, fmtNum } from './format';
import { Tile, alert, card, grid, muted, td, tdR, th, thR } from './Overview';

// "Analiza wydajności przewijania": per position how much was rewound, how long it took and
// what was left over (waste / grind / przejściówka), with every big spool of the position.

type Status = 'in_progress' | 'done';

interface SpoolRow {
  id: number;
  label: string;
  labelNumber: number;
  weightKg: number;
  printedAt: string | null;
  rewound: boolean;
  rewinder: string | null;
  operator: string | null;
  shift: 'zmiana_1' | 'zmiana_2' | null;
  startedAt: string | null;
  finishedAt: string | null;
  minutes: number | null;
  pcs: number | null;
  pcsPerH: number | null;
  productKg: number | null;
  restKg: number | null;
  restFate: 'waste' | 'grind' | null;
  transitionKg: number | null;
}

interface ItemRow {
  id: number;
  week: string | null;
  line: string | null;
  sku: string | null;
  name: string | null;
  material: string | null;
  plan: number | null;
  pieceKg: number | null;
  status: Status;
  spoolCount: number;
  spoolsRewound: number;
  spoolKg: number;
  rewoundSpoolKg: number;
  pcs: number;
  manualPcs: number;
  totalPcs: number;
  productKg: number | null;
  wasteKg: number;
  grindKg: number;
  transitionKg: number;
  minutes: number;
  pcsPerH: number | null;
  kgPerH: number | null;
  rewinders: string[];
  operators: string[];
  last_activity_at: string | null;
  spools: SpoolRow[];
}

interface Data {
  summary: {
    rewinds: number;
    pcs: number;
    manualPcs: number;
    productKg: number;
    spoolKg: number;
    wasteKg: number;
    grindKg: number;
    transitionKg: number;
    minutes: number;
    pcsPerH: number | null;
    kgPerH: number | null;
    inProgress: number;
  };
  items: ItemRow[];
}

const dt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const kg = (v: number | null | undefined, digits = 2) => (v === null || v === undefined ? '—' : `${fmtNum(v, digits)} kg`);
const kgOrDash = (v: number) => (v > 0 ? kg(v) : '—');

const Rewinding: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'' | Status>('');
  const [rewinder, setRewinder] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const { compare, material_id, ...rest } = toQuery(filters);
    api.get('/analytics/rewinding', { params: rest })
      .then(res => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.message ?? t('analytics.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, t]);

  const rewinders = useMemo(() => Array.from(new Set((data?.items ?? []).flatMap(i => i.rewinders))).sort(), [data]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.items ?? []).filter(i =>
      (!status || i.status === status)
      && (!rewinder || i.rewinders.includes(rewinder))
      && (!q || [i.sku, i.name, i.line, i.week, ...i.operators, ...i.spools.map(s => s.label)]
        .some(v => v && v.toLowerCase().includes(q))));
  }, [data, status, rewinder, search]);

  const toggle = (id: number) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const s = data?.summary;

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🧵 {t('rewindingReport.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('rewindingReport.subtitle')}</p>
      </div>
      <AnalyticsFilterBar filters={filters} onChange={setFilters} showCompare={false} showMaterial={false} />
      {error && <div style={alert}>{error}</div>}
      {loading && !data && <div style={{ color: '#94a3b8', padding: 32, textAlign: 'center' }}>{t('common.loading')}</div>}

      {data && s && (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <div style={{ ...muted, marginBottom: 8 }}>{t('rewindingReport.summaryHint')}</div>
          <div style={grid}>
            <Tile label={t('rewindingReport.tilePcs')} value={fmtNum(s.pcs)} sub={s.manualPcs ? t('rewindingReport.tileManual', { pcs: fmtNum(s.manualPcs) }) : t('rewindingReport.tileRewinds', { count: s.rewinds })} delta={null} vsLabel="" />
            <Tile label={t('rewindingReport.tileKg')} value={kg(s.productKg, 1)} sub={t('rewindingReport.tileSpoolKg', { kg: kg(s.spoolKg, 1) })} delta={null} vsLabel="" />
            <Tile label={t('rewindingReport.tileTime')} value={fmtMin(s.minutes)} delta={null} vsLabel="" />
            <Tile label={t('rewindingReport.tileRate')} value={s.pcsPerH === null ? '—' : `${fmtNum(s.pcsPerH, 1)} ${t('rewindingReport.pcsPerH')}`} sub={s.kgPerH === null ? undefined : `${fmtNum(s.kgPerH, 1)} kg/h`} delta={null} vsLabel="" />
            <Tile label={t('rewindingReport.tileWaste')} value={kg(s.wasteKg)} delta={null} vsLabel="" />
            <Tile label={t('rewindingReport.tileGrind')} value={kg(s.grindKg)} delta={null} vsLabel="" />
            <Tile label={t('rewindingReport.tileTransition')} value={kg(s.transitionKg)} delta={null} vsLabel="" />
            <Tile label={t('rewindingReport.tileInProgress')} value={fmtNum(s.inProgress)} delta={null} vsLabel="" warn={s.inProgress > 0} />
          </div>

          <div style={card}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <select value={status} onChange={e => setStatus(e.target.value as '' | Status)} style={control}>
                <option value="">{t('rewindingReport.statusAll')}</option>
                <option value="in_progress">{t('rewindingReport.status_in_progress')}</option>
                <option value="done">{t('rewindingReport.status_done')}</option>
              </select>
              <select value={rewinder} onChange={e => setRewinder(e.target.value)} style={control}>
                <option value="">{t('rewindingReport.rewinderAll')}</option>
                {rewinders.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('rewindingReport.searchPlaceholder')} style={{ ...control, minWidth: 260 }} />
              <span style={{ ...muted, marginLeft: 'auto' }}>{t('rewindingReport.positions', { count: rows.length })}</span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={th} />
                    <th style={th}>{t('rewindingReport.col.week')}</th>
                    <th style={th}>{t('rewindingReport.col.line')}</th>
                    <th style={th}>{t('rewindingReport.col.position')}</th>
                    <th style={th}>{t('rewindingReport.col.status')}</th>
                    <th style={thR}>{t('rewindingReport.col.spools')}</th>
                    <th style={thR}>{t('rewindingReport.col.rewound')}</th>
                    <th style={thR}>{t('rewindingReport.col.productKg')}</th>
                    <th style={thR}>{t('rewindingReport.col.waste')}</th>
                    <th style={thR}>{t('rewindingReport.col.grind')}</th>
                    <th style={thR}>{t('rewindingReport.col.transition')}</th>
                    <th style={thR}>{t('rewindingReport.col.time')}</th>
                    <th style={thR}>{t('rewindingReport.col.rate')}</th>
                    <th style={th}>{t('rewindingReport.col.who')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={14} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 28 }}>{t('rewindingReport.empty')}</td></tr>
                  ) : rows.map(i => {
                    const open = expanded.has(i.id);
                    return (
                      <React.Fragment key={i.id}>
                        <tr onClick={() => toggle(i.id)} style={{ cursor: 'pointer', background: open ? '#f8fafc' : undefined }}>
                          <td style={{ ...td, color: '#94a3b8', width: 18 }}>{open ? '▾' : '▸'}</td>
                          <td style={td}>{i.week ?? '—'}</td>
                          <td style={td}>{i.line ?? '—'}</td>
                          <td style={{ ...td, whiteSpace: 'normal', minWidth: 200 }}>
                            <b>{i.sku ?? `#${i.id}`}</b>
                            <div style={muted}>{[i.name, i.material].filter(Boolean).join(' · ')}</div>
                          </td>
                          <td style={td}><StatusBadge status={i.status} label={t(`rewindingReport.status_${i.status}`)} /></td>
                          <td style={tdR}>
                            {i.spoolCount > 0 ? `${i.spoolsRewound}/${i.spoolCount}` : '—'}
                            {i.spoolCount > 0 && <div style={muted}>{kg(i.spoolKg, 1)}</div>}
                          </td>
                          <td style={tdR}>
                            <b>{fmtNum(i.totalPcs)}</b>{i.plan !== null && <span style={muted}> / {fmtNum(i.plan)}</span>}
                            {i.manualPcs !== 0 && <div style={muted}>{t('rewindingReport.manualPcs', { pcs: fmtNum(i.manualPcs) })}</div>}
                          </td>
                          <td style={tdR}>{kg(i.productKg, 1)}</td>
                          <td style={{ ...tdR, color: i.wasteKg > 0 ? '#dc2626' : undefined }}>{kgOrDash(i.wasteKg)}</td>
                          <td style={tdR}>{kgOrDash(i.grindKg)}</td>
                          <td style={tdR}>{kgOrDash(i.transitionKg)}</td>
                          <td style={tdR}>{i.minutes > 0 ? fmtMin(i.minutes) : '—'}</td>
                          <td style={tdR}>
                            {i.pcsPerH === null ? '—' : `${fmtNum(i.pcsPerH, 1)} ${t('rewindingReport.pcsPerH')}`}
                            {i.kgPerH !== null && <div style={muted}>{fmtNum(i.kgPerH, 1)} kg/h</div>}
                          </td>
                          <td style={{ ...td, whiteSpace: 'normal', minWidth: 140 }}>
                            {i.operators.join(', ') || '—'}
                            {i.rewinders.length > 0 && <div style={muted}>{i.rewinders.join(', ')}</div>}
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={14} style={{ padding: '8px 12px 16px 36px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                              <SpoolTable spools={i.spools} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <p style={{ ...muted, marginTop: 4 }}>{t('rewindingReport.methodHint')}</p>
        </div>
      )}
    </div>
  );
};

const SpoolTable: React.FC<{ spools: SpoolRow[] }> = ({ spools }) => {
  const { t } = useLanguage();
  if (spools.length === 0) {
    return <div style={muted}>{t('rewindingReport.noSpools')}</div>;
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, background: '#fff', border: '1px solid #e2e8f0' }}>
      <thead>
        <tr>
          <th style={th}>{t('rewindingReport.spool.label')}</th>
          <th style={thR}>{t('rewindingReport.spool.weight')}</th>
          <th style={th}>{t('rewindingReport.spool.rewinder')}</th>
          <th style={th}>{t('rewindingReport.spool.operator')}</th>
          <th style={th}>{t('rewindingReport.spool.shift')}</th>
          <th style={th}>{t('rewindingReport.spool.start')}</th>
          <th style={th}>{t('rewindingReport.spool.end')}</th>
          <th style={thR}>{t('rewindingReport.spool.time')}</th>
          <th style={thR}>{t('rewindingReport.spool.pcs')}</th>
          <th style={thR}>{t('rewindingReport.spool.rate')}</th>
          <th style={thR}>{t('rewindingReport.spool.rest')}</th>
          <th style={thR}>{t('rewindingReport.spool.transition')}</th>
        </tr>
      </thead>
      <tbody>
        {spools.map(sp => (
          <tr key={sp.id}>
            <td style={td}>
              <b>{sp.label}</b> <span style={muted}>#{sp.labelNumber}</span>
              <div style={muted}>{t('rewindingReport.spool.printed', { at: dt(sp.printedAt) })}</div>
            </td>
            <td style={tdR}>{kg(sp.weightKg, 2)}</td>
            {sp.rewound ? (
              <>
                <td style={td}>{sp.rewinder ?? '—'}</td>
                <td style={td}>{sp.operator ?? '—'}</td>
                <td style={td}>{sp.shift ? t(sp.shift === 'zmiana_1' ? 'production.shift1' : 'production.shift2') : '—'}</td>
                <td style={td}>{dt(sp.startedAt)}</td>
                <td style={td}>{dt(sp.finishedAt)}</td>
                <td style={tdR}>{fmtMin(sp.minutes)}</td>
                <td style={tdR}><b>{fmtNum(sp.pcs)}</b>{sp.productKg !== null && <div style={muted}>{kg(sp.productKg, 1)}</div>}</td>
                <td style={tdR}>{sp.pcsPerH === null ? '—' : `${fmtNum(sp.pcsPerH, 1)} ${t('rewindingReport.pcsPerH')}`}</td>
                <td style={tdR}>
                  {sp.restKg ? kg(sp.restKg) : '—'}
                  {sp.restKg && sp.restFate ? <div style={{ ...muted, color: sp.restFate === 'waste' ? '#dc2626' : '#64748b' }}>{t(`rewindingReport.fate_${sp.restFate}`)}</div> : null}
                </td>
                <td style={tdR}>{sp.transitionKg ? kg(sp.transitionKg) : '—'}</td>
              </>
            ) : (
              <td colSpan={10} style={{ ...td, color: '#b45309' }}>{t('rewindingReport.spoolWaiting')}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const StatusBadge: React.FC<{ status: Status; label: string }> = ({ status, label }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
    background: status === 'done' ? '#dcfce7' : '#fef3c7',
    color: status === 'done' ? '#166534' : '#92400e',
  }}>
    {label}
  </span>
);

const control: React.CSSProperties = { padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff' };

export default Rewinding;
